import { useContext, useMemo } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import type { ExtranetPackageItem } from 'app/typings/core/extranet/packages/packages';

import { formatExperienceName } from '../../../ExtranetInventory/Modern/_components/TabViews/tabViewUtils';
import type { AddOn } from '../_components/Tables/AddOnsTable/AddOnsTable';
import { extractPrices } from '../_utils/packContent.utils';

export const usePackAddOns = (selectedPack?: ExtranetPackageItem): AddOn[] => {
  const { data: experienceCategories } = useExperiencesCategories();
  const { getExperience } = useContext(ModernHotelContext);

  return useMemo(() => {
    if (!selectedPack) {
      return [];
    }

    return selectedPack.experiences.reduce<AddOn[]>((acc, pe) => {
      if (pe.isIncluded) {
        return acc;
      }

      const exp = getExperience(pe.experienceId);
      const categoryId = exp?.categoryId ?? pe.categoryId;
      const category = experienceCategories?.find(
        (cat) => cat.id === categoryId
      )?.name;

      const name = exp
        ? formatExperienceName(exp, category)
        : (category ?? `Experience #${pe.experienceId}`);
      const { adultPrice, childPrice } = extractPrices(
        pe.openingConfiguration,
        selectedPack.isDaytime
      );
      const areAdultsAllowed = exp?.allowedPaxTypes?.includes(PAX_TYPE.ADULT);
      const areChildrenAllowed = exp?.allowedPaxTypes?.includes(PAX_TYPE.CHILD);

      acc.push({
        id: pe.experienceId.toString(),
        name: name ?? `Experience #${pe.experienceId}`,
        images: pe.emoji ? [pe.emoji] : undefined,
        adultPrice,
        childPrice,
        areAdultsAllowed,
        areChildrenAllowed,
        maxQuantityPerBooking: exp?.maxQuantity,
        isOriginals: exp ? !!exp.countryId : true,
        configuration: {
          quantity: pe.quantity,
          isPerPerson: pe.isPerPerson,
          isPerNight: pe.isPerNight,
          isOptional: pe.isOptional,
        },
      });

      return acc;
    }, []);
  }, [selectedPack, getExperience, experienceCategories]);
};
